import { Check } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { cn } from '../../lib/utils.ts';
import { clampShare } from './capsule-bar.tsx';

/** Innere Maße der Grafik (viewBox); sichtbar gezeichnet wird in `--ring-size` mit Strich `--ring-stroke` (DESIGN `sizes`). */
const SIZE = 112;
const STROKE = 10;
const RADIUS = (SIZE - STROKE) / 2;

/** Größen des Rings (DESIGN `sizes`): klein in Kacheln, normal, groß auf der Bühne der Stempel-Karte. */
const RING_SIZE = { xs: 'size-[var(--ring-size-xs)]', sm: 'size-[var(--ring-size-sm)]', md: 'size-[var(--ring-size)]', lg: 'size-[var(--ring-size-lg)]' } as const;
/** Wert in der Mitte je Größe (benannte Schriftgrößen). */
const RING_VALUE = { xs: 'text-micro', sm: 'text-caption', md: 'text-kpi', lg: 'text-hero-sm' } as const;

/**
 * Anteil für die Anzeige als ganze Prozentzahl (abgerundet, damit „100 %“ erst bei erreichtem Ziel steht). Rein, nur
 * Darstellung: `value` und `target` in derselben Einheit (z. B. Minuten); Ziel ≤ 0 → `null` (kein Ring-Anteil).
 */
export function ringPercent(value: number, target: number): number | null {
  if (!Number.isFinite(value) || !Number.isFinite(target) || target <= 0) return null;
  const v = Math.max(0, Math.trunc(value));
  return Math.floor((v * 100) / Math.trunc(target));
}

/**
 * Fortschritts-Ring (Vorbild Apple Fitness; erster Einsatz Zeiterfassung › Kalender, Kachel „Resturlaub“, Story 22.8;
 * Messwert seit Ticket 1.284): Spur `line`, Füllung `s3-capsule-fill` wie der Kapselbalken (DESIGN „Fortschritts-Ring“;
 * auf dem Band weiß über `accent-brand`, Regel in styles.css), Anteil 0–1 (gedeckelt),
 * Start oben, im Uhrzeigersinn, Größe `--ring-size`, Strich `--ring-stroke`. In der Mitte der Wert (`children`, z. B. „24“
 * oder „52 %“), darunter optional eine kleine Zeile („von 30 Tagen“). Die Füllung läuft beim Erscheinen und bei jeder
 * Änderung mit `motion-slow` an („Bewegung reduzieren“: sofort).
 *
 * - ohne `meter`: ein Bild mit Satz (`label`), die Grafik selbst ist stumm (Kalender);
 * - `meter`: Messwert (`role="meter"`, 0–100, `aria-valuetext` z. B. „4:12 h von 8:00 h“), Name `label`;
 * - `done`: Ziel erreicht – voller Ring und Haken statt Zahl (EK9, EXPERIENCE §1.2 S2);
 * - `size`: `xs` (`--ring-size-xs`, Kopf einer Gruppe, ohne Wert), `sm` (`--ring-size-sm`, Kacheln, ohne Unterzeile), `md` (Standard), `lg` (`--ring-size-lg`, Stempel-Karte).
 * - `tone`: `check` = Füllung Bernstein (`check-dot`) für eine laufende Frist (Countdown „noch 28 Tage“, Story 22.9).
 * Keine Dauer-Animation (EK5): nur das einmalige Füllen.
 * Gerechnet wird hier nichts außer der Bogenlänge aus dem Anteil.
 */
export function ProgressRing({
  value,
  label,
  children,
  caption,
  className,
  testId,
  meter,
  done = false,
  size = 'md',
  track = 'line',
  tone = 'capsule',
}: {
  value: number;
  label: string;
  children?: ReactNode;
  caption?: ReactNode;
  className?: string;
  testId?: string;
  /** Messwert: vorgelesener Wert und ganze Prozentzahl (`ringPercent`; ohne Angabe gerundet aus dem Anteil). */
  meter?: { valueText: string; now?: number | null };
  done?: boolean;
  size?: keyof typeof RING_SIZE;
  /** Farbe der Spur: `line` (auf Karte), `card` (auf getönter Fläche wie der Bühne der Stempel-Karte). */
  track?: 'line' | 'card';
  /** Farbe der Füllung: `capsule` (Standard) oder `check` (Bernstein, Frist läuft). */
  tone?: 'capsule' | 'check';
}) {
  const share = done ? 1 : clampShare(value);
  const filled = Math.round(share * 1000) / 10;
  // Erst leer zeichnen, dann im nächsten Bild füllen: so läuft der Übergang auch beim ersten Erscheinen.
  // Bei „Bewegung reduzieren“ gleich voll zeichnen (kein leeres Bild).
  const [drawn, setDrawn] = useState(() => (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? filled : 0));
  useEffect(() => {
    const frame = requestAnimationFrame(() => setDrawn(filled));
    return () => cancelAnimationFrame(frame);
  }, [filled]);
  const a11y = meter
    ? { role: 'meter', 'aria-label': label, 'aria-valuemin': 0, 'aria-valuemax': 100, 'aria-valuenow': done ? 100 : Math.min(100, meter.now ?? Math.round(share * 100)), 'aria-valuetext': meter.valueText }
    : { role: 'img', 'aria-label': label };
  return (
    <div
      {...a11y}
      data-testid={testId}
      data-progress-ring=""
      data-done={done ? '' : undefined}
      data-size={size}
      data-tone={tone === 'check' ? 'check' : undefined}
      className={cn('relative grid flex-none place-items-center', RING_SIZE[size], className)}
    >
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden="true" focusable="false" className="absolute inset-0 size-full -rotate-90">
        <circle cx={SIZE / 2} cy={SIZE / 2} r={RADIUS} fill="none" className={cn(track === 'card' ? 'stroke-card' : 'stroke-[var(--line)]', '[stroke-width:var(--ring-stroke)]')} />
        {filled > 0 ? (
          <circle
            data-ring-fill=""
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            strokeLinecap="round"
            pathLength={100}
            strokeDasharray={`${drawn} 100`}
            className={cn(
              tone === 'check' ? 'stroke-check-dot' : 'stroke-capsule',
              '[stroke-width:var(--ring-stroke)] transition-[stroke-dasharray,opacity] duration-(--motion-slow) ease-(--motion-ease) motion-reduce:transition-none',
              drawn === 0 && 'opacity-0',
            )}
          />
        ) : null}
      </svg>
      <div aria-hidden className="relative flex min-w-0 flex-col items-center gap-space-1 text-center leading-none">
        {done ? (
          <Check data-ring-check="" size={size === 'xs' ? 12 : size === 'sm' ? 18 : size === 'lg' ? 48 : 36} strokeWidth={2.25} className="animate-fade-in text-ink" />
        ) : (
          <span className={cn(RING_VALUE[size], 'font-medium text-ink tabular-nums')}>{children}</span>
        )}
        {caption && size !== 'sm' && size !== 'xs' ? <span className="type-meta text-ink-3">{caption}</span> : null}
      </div>
    </div>
  );
}
